import { useUserProfile } from '@/context/UserProfileContext';
import { useToast } from '@/hooks/use-toast';
import { Check, CircleX, UserCheck, UserPlus, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { api } from '@/utils/api';
import { IonButton } from '@ionic/react';

const FRIENDSHIP_STATUSES = {
  NONE: 'none', // No relationship
  PENDING: 'pending', // Friend request sent
  RECEIVED: 'received', // Friend request received
  FRIENDS: 'friends', // Already friends
  ACCEPTED: 'accepted', // Friend request accepted
};

type FriendshipButtonProps = {
  userId?: string;
  dontCheckOnInit?: boolean;
  withIcon?: boolean;
};

const FriendshipButton: React.FC<FriendshipButtonProps> = ({ userId, dontCheckOnInit, withIcon }) => {
  const { t } = useTranslation();
  const [friendshipStatus, setFriendshipStatus] = useState(FRIENDSHIP_STATUSES.NONE);
  const [loading, setLoading] = useState(false);
  const { profile } = useUserProfile();
  const { toast } = useToast();
  // Fetch friendship status when the component loads
  useEffect(() => {
    const fetchFriendshipStatus = async () => {
      if (!profile) return;
      if (!userId || dontCheckOnInit) return;

      try {
        const { data } = await api.get(`/friendships?receiverId=${userId}`);
        setFriendshipStatus(data.status);
      } catch (err) {
        console.error('Failed to fetch friendship status:', err);
      }
    };

    fetchFriendshipStatus();
  }, [profile, userId, dontCheckOnInit]);

  // Handle friend request actions (send, cancel, accept, decline)
  const handleFriendRequest = async (action: string) => {
    try {
      setLoading(true);
      const payload = action === 'accept' || action === 'decline'
        ? { senderId: userId }
        : { receiverId: userId };

      let response;
      switch (action) {
        case 'accept':
          response = await api.put('/friendships/', payload);
          break;
        case 'decline':
          console.log('decline', userId, profile?.userId);
          response = await api.delete('/friendships', {
            data: {
              senderId: userId,
              receiverId: profile?.userId
            }
          });
          break;
        case 'cancel':
          response = await api.delete('/friendships', {
            body: {
              senderId: profile?.userId,
              receiverId: userId
            }
          });
          break;
        default: // send
          response = await api.post('/friendships', payload);
      }

      setFriendshipStatus(response.data.status);
    } catch (err) {
      console.error(`Error while trying to ${action} friend request:`, err);
      toast({
        description: (
          <span className="flex">
            <CircleX className="mr-2 text-destructive" />
            <span>Failed to send friend request</span>
          </span>
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  // Render the button based on the friendship status
  const renderButton = () => {
    if (!profile) return null; // Wait for profile to load
    if (profile.userId === userId) return null; // No button for the profile owner

    switch (friendshipStatus) {
      case FRIENDSHIP_STATUSES.NONE:
        return (
          <IonButton fill="outline" className="text-foreground" disabled={loading} onClick={() => handleFriendRequest('send')}>
            {withIcon && <UserPlus />}
            {t('follow')}
          </IonButton>
        );

      case FRIENDSHIP_STATUSES.PENDING:
        return (
          <IonButton fill="outline" className="text-foreground" disabled={loading} onClick={() => handleFriendRequest('cancel')}>
            <XCircle />
            {t('cancel')}
          </IonButton>
        );

      case FRIENDSHIP_STATUSES.RECEIVED:
        return (
          <div className="flex space-x-2">
            <IonButton fill="outline" className="text-foreground" type="button" disabled={loading} onClick={() => handleFriendRequest('accept')}>
              {withIcon && <Check />}
              {t('accept_friend_request')}
            </IonButton>
            <IonButton fill="outline" className="text-foreground" type="button" disabled={loading} onClick={() => handleFriendRequest('decline')}>
              {withIcon && <XCircle />}
              {t('ignore')}
            </IonButton>
          </div>
        );

      case FRIENDSHIP_STATUSES.FRIENDS:
        return (
          <IonButton fill="outline" className="text-foreground" type="button" disabled={loading} onClick={() => handleFriendRequest('accept')}>
            {withIcon && <UserCheck />}
            {t('followed')}
          </IonButton>
        );

      case FRIENDSHIP_STATUSES.ACCEPTED:
        return (
          <IonButton fill="outline" className="text-foreground" type="button" disabled={loading} onClick={() => handleFriendRequest('accept')}>
            {withIcon && <UserCheck />}
            {t('followed')}
          </IonButton>
        );
      default:
        return <h2>nope</h2>;
    }
  };

  return renderButton();
};

export default FriendshipButton;
